// ###### IMPORTS
import React, { useState, useEffect, useCallback } from 'react';
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell, LabelList,
} from 'recharts';
import { BanknotesIcon, ChartBarIcon, ChevronDownIcon, DocumentIcon } from '@heroicons/react/24/solid';
import FabAssetsMenu from './viewComponents/Common/FabAssetsMenu';
import { useAssetsData } from '../moduleServices/assets_data_context';
import { computeAssetBalanceSummary } from './viewServices/assets_balance_summary';
import BoardFilterAssets from './viewComponents/Board/BoardFilterAssets';
import BoardTableSnapshots from './viewComponents/Board/BoardTableSnapshots';
import CardEvolutionHistory from './viewComponents/Board/CardEvolutionHistory';
import { buildHierarchy, sortedLevel1Keys, buildLevel2Grouping, sortedLevel2KeysFromGrouping } from './viewComponents/Detailed/CardAssetsDetailed';
import { formatDate } from 'shared_items/formatConventions';
import {
  ASSETS_CATEGORY_COLORS as CATEGORY_COLORS,
  formatCompactCurrencyValue as formatValue,
  buildSnapshotMapWithMarketValue as buildSnapshotMap,
  sumSnapshotValueFromMap as sumSnapshotValue,
  sumMarketValueFromMap as sumMarketValue,
} from './viewServices/assets_helpers';
import { readAssetsBoardState, persistAssetsBoardState } from './viewServices/assets_board_storage';

// ###### CARD: TOTAL PATRIMONY (total + 100% stacked bar by L0, same data as BoardTableSnapshots)
const CardTotalPatrimony = ({ total, totalMarket, valueMode, latestSnapshot, loading, assets = [], categories = [], snapshotMap = {} }) => {
  const [chartHover, setChartHover] = useState({ index: null, x: 0, y: 0 });
  const useMarket = valueMode === 'market';
  const displayTotal = useMarket ? (totalMarket ?? total) : total;
  const sumFn = useMarket ? sumMarketValue : sumSnapshotValue;

  const byL0 = React.useMemo(() => {
    const hierarchy = buildHierarchy(assets, categories);
    const sortedL1 = sortedLevel1Keys(hierarchy);
    const list = sortedL1.map((l1) => {
      const assetsInL1 = (Object.keys(hierarchy[l1] || {})).flatMap((l2) => hierarchy[l1][l2] || []);
      const value = sumFn(assetsInL1, snapshotMap);
      return { name: l1, value };
    }).filter((x) => x.value > 0);
    const totalSum = list.reduce((s, x) => s + x.value, 0);
    return list
      .map((x) => ({ ...x, percentage: totalSum > 0 ? (x.value / totalSum) * 100 : 0 }))
      .sort((a, b) => b.percentage - a.percentage);
  }, [assets, categories, snapshotMap, valueMode]);

  const hasChartData = byL0.length > 0 && displayTotal > 0;
  const chartHeight = 160;

  return (
    <div className="bg-white shadow rounded-xl overflow-hidden p-3 flex flex-col h-full min-h-[200px]">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap shrink-0">
        <div className="flex items-center gap-2">
          <BanknotesIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
          <span className="text-md font-semibold text-gray-800">Patrimony</span>
        </div>
        {latestSnapshot && (
          <span className="text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5 font-medium">
            {formatDate(latestSnapshot.snapshot_date, 'display')}
          </span>
        )}
      </div>
      {loading ? (
        <div className="grid grid-cols-2 gap-4 flex-1 min-h-0 w-full items-center justify-items-center">
          <div className="flex flex-col items-center justify-center w-full h-full min-h-[120px]">
            <div className="animate-pulse h-8 w-24 bg-gray-200 rounded" />
          </div>
          <div className="flex items-center justify-center w-full h-full min-h-[120px]">
            <div className="flex gap-3 items-stretch w-full" style={{ height: chartHeight }}>
              <div className="w-16 shrink-0 rounded overflow-hidden bg-gray-200 animate-pulse" />
              <div className="flex-1 min-w-0 flex flex-col gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="animate-pulse h-5 bg-gray-200 rounded flex-shrink-0" style={{ width: `${100 - i * 15}%` }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : displayTotal === 0 && !latestSnapshot ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <DocumentIcon className="w-16 h-16 mx-auto text-gray-400 mb-3" />
            <div className="text-gray-500 text-sm font-medium">No assets registered yet.</div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 flex-1 min-h-0 w-full items-center justify-items-center">
          <div className="flex flex-col items-center justify-center w-full h-full min-h-[120px]">
            <p className="text-2xl font-bold text-green-800 text-center leading-tight">
              R$ {formatValue(displayTotal)}
            </p>
            <span className="mt-1 text-sm font-medium text-green-800">
              {useMarket ? 'market value' : 'base value'}
            </span>
          </div>
          <div className="flex items-center justify-center w-full h-full min-h-[120px]">
            {hasChartData ? (
              <div className="flex gap-3 items-stretch relative" style={{ height: chartHeight }}>
                <div className="flex flex-col w-16 shrink-0 rounded overflow-hidden border border-gray-200">
                  {byL0.map((item, index) => (
                    <div
                      key={item.name}
                      className="flex items-center justify-center flex-shrink-0 cursor-pointer"
                      style={{
                        height: `${item.percentage}%`,
                        backgroundColor: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
                      }}
                      onMouseEnter={(e) => setChartHover({ index, x: e.clientX, y: e.clientY })}
                      onMouseMove={(e) => setChartHover((prev) => (prev.index === index ? { ...prev, x: e.clientX, y: e.clientY } : prev))}
                      onMouseLeave={() => setChartHover({ index: null, x: 0, y: 0 })}
                    >
                      <span className="text-white text-[10px] font-semibold">{Math.round(item.percentage * 10) / 10}%</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  {byL0.map((item, index) => (
                    <div
                      key={item.name}
                      className="flex items-center pl-2 flex-shrink-0 text-gray-800 text-xs"
                      style={{ height: `${item.percentage}%` }}
                    >
                      {item.name}
                    </div>
                  ))}
                </div>
                {chartHover.index !== null && byL0[chartHover.index] && (
                  <div
                    className="fixed z-50 px-2 py-1.5 bg-white text-gray-800 text-xs font-medium rounded shadow-lg border border-gray-200 pointer-events-none"
                    style={{
                      left: chartHover.x + 10,
                      top: chartHover.y - 8,
                    }}
                  >
                    <div className="font-semibold text-gray-700">{byL0[chartHover.index].name}</div>
                    <div>R$ {formatValue(byL0[chartHover.index].value)}</div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

// ###### CARD: BREAKDOWN BY CATEGORY / LOCATION / SUB-CATEGORY
const GROUP_OPTIONS = [
  { value: 'location', label: 'By Location' },
  { value: 'category', label: 'By Category' },
  { value: 'subcategory', label: 'By Sub-Category' },
];

const CardCategoryBreakdown = ({
  byCategoryBase = [], byCategoryMarket = [], byLocationBase = [], byLocationMarket = [], bySubCategoryBase = [], bySubCategoryMarket = [],
  valueMode = 'base', total, loading, latestSnapshot,
  groupBy = 'location',
  onGroupByChange,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const byCategory = valueMode === 'market' ? byCategoryMarket : byCategoryBase;
  const byLocation = valueMode === 'market' ? byLocationMarket : byLocationBase;
  const bySubCategory = valueMode === 'market' ? bySubCategoryMarket : bySubCategoryBase;
  const data = groupBy === 'location' ? byLocation : groupBy === 'category' ? byCategory : bySubCategory;
  const labelKey = groupBy === 'location' ? 'location_name' : groupBy === 'category' ? 'category_code' : 'subcategory_code';
  const currentLabel = GROUP_OPTIONS.find((o) => o.value === groupBy)?.label ?? 'By Location';

  return (
    <div className="bg-white shadow rounded-xl overflow-hidden p-3">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2" ref={dropdownRef}>
          <ChartBarIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
          <div className="relative">
            <button
              type="button"
              onClick={() => setDropdownOpen((o) => !o)}
              className="inline-flex items-center gap-1 text-md font-semibold text-gray-800 hover:text-gray-900 focus:outline-none"
            >
              {currentLabel}
              <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            </button>
            {dropdownOpen && (
              <div className="absolute left-0 top-full mt-0.5 bg-white border border-gray-200 rounded-md shadow-lg py-1 z-10 min-w-[140px]">
                {GROUP_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onGroupByChange?.(opt.value);
                      setDropdownOpen(false);
                    }}
                    className={`block w-full text-left px-3 py-1.5 text-sm ${groupBy === opt.value ? 'font-semibold text-gray-900 bg-gray-50' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {latestSnapshot && (
          <span className="text-xs text-gray-500 bg-gray-100 rounded px-2 py-0.5 font-medium">
            {formatDate(latestSnapshot.snapshot_date, 'display')}
          </span>
        )}
      </div>
      <div>
        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse h-5 bg-gray-200 rounded" style={{ width: `${80 - i * 10}%` }} />
            ))}
          </div>
        ) : data.length === 0 ? (
          <div className="flex items-center justify-center h-48">
            <div className="text-center">
              <DocumentIcon className="w-16 h-16 mx-auto text-gray-400 mb-3" />
              <div className="text-gray-500 text-sm font-medium">No data available.</div>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(data.length * 36, 80)}>
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 4, right: 60, bottom: 4, left: 8 }}
            >
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey={labelKey}
                width={90}
                tick={{ fontSize: 11, fill: '#374151' }}
              />
              <Tooltip
                formatter={(value) => [`R$ ${formatValue(value)}`, 'Value']}
                labelStyle={{ fontSize: 12 }}
                contentStyle={{ fontSize: 12 }}
              />
              <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                {data.map((entry, index) => (
                  <Cell key={entry[labelKey] ?? index} fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} />
                ))}
                <LabelList
                  dataKey="total"
                  position="right"
                  formatter={(v) => formatValue(v)}
                  style={{ fontSize: 11, fill: '#374151' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

// ###### MAIN COMPONENT
const AssetsOverview = () => {
  const {
    assets,
    categories,
    snapshots,
    snapshot_items: snapshotItems,
    loading,
    error,
    refreshFull,
  } = useAssetsData();

  const [snapshotMap, setSnapshotMap] = useState({});
  const [snapshotDate, setSnapshotDate] = useState(null);
  const [selectedSnapshotId1, setSelectedSnapshotId1] = useState(null);
  const [selectedSnapshotId2, setSelectedSnapshotId2] = useState(null);
  const [snapshotMaps, setSnapshotMaps] = useState([]);
  const [snapshotDates, setSnapshotDates] = useState([]);

  const [board, setBoard] = useState(readAssetsBoardState);
  const patchBoard = useCallback((partial) => {
    setBoard((prev) => {
      const next = { ...prev, ...partial };
      persistAssetsBoardState(next);
      return next;
    });
  }, []);

  const { valueMode, groupBy } = board;

  const [selectedAssetIdFilter, setSelectedAssetIdFilter] = useState(null);
  const [selectedCategoryCodeFilter, setSelectedCategoryCodeFilter] = useState('');
  const [selectedLocationNameFilter, setSelectedLocationNameFilter] = useState('');

  const summaryFilters = React.useMemo(
    () => ({
      ...(selectedAssetIdFilter ? { asset: selectedAssetIdFilter } : {}),
      ...(selectedCategoryCodeFilter ? { category_code: selectedCategoryCodeFilter } : {}),
      ...(selectedLocationNameFilter ? { location_name: selectedLocationNameFilter } : {}),
    }),
    [selectedAssetIdFilter, selectedCategoryCodeFilter, selectedLocationNameFilter]
  );

  const summary = React.useMemo(
    () => computeAssetBalanceSummary({ snapshots, snapshot_items: snapshotItems, assets }, summaryFilters),
    [snapshots, snapshotItems, assets, summaryFilters]
  );

  const snapshotList = React.useMemo(() => {
    const list = [...snapshots];
    return list.sort((a, b) => {
      const da = a.snapshot_date || '';
      const db = b.snapshot_date || '';
      return db.localeCompare(da);
    });
  }, [snapshots]);

  const loadItemsForTwoSnapshots = useCallback(
    (id1, id2, listRef = null) => {
      const ids = [id1, id2].filter(Boolean);
      if (ids.length === 0) {
        setSnapshotMaps([]);
        setSnapshotDates([]);
        setSnapshotMap({});
        setSnapshotDate(null);
        return;
      }
      const list = listRef && listRef.length ? listRef : snapshotList;
      const maps = ids.map((id) =>
        buildSnapshotMap(snapshotItems.filter((i) => i.snapshot === id))
      );
      const dates = ids.map((id) => list.find((s) => s.id === id)?.snapshot_date ?? null);
      setSnapshotMaps(maps);
      setSnapshotDates(dates);
      const idx1 = id1 != null ? ids.indexOf(id1) : -1;
      setSnapshotMap(idx1 >= 0 ? maps[idx1] ?? {} : {});
      setSnapshotDate(dates[0] ?? null);
    },
    [snapshotList, snapshotItems]
  );

  useEffect(() => {
    if (!snapshotList.length) {
      setSelectedSnapshotId1(null);
      setSelectedSnapshotId2(null);
      return;
    }
    const twoLatest = snapshotList.slice(0, 2);
    setSelectedSnapshotId1(twoLatest[0]?.id ?? null);
    setSelectedSnapshotId2(twoLatest[1]?.id ?? null);
  }, [summaryFilters, snapshotList]);

  useEffect(() => {
    loadItemsForTwoSnapshots(selectedSnapshotId1, selectedSnapshotId2, snapshotList);
  }, [selectedSnapshotId1, selectedSnapshotId2, snapshotList, snapshotItems, loadItemsForTwoSnapshots]);

  const handleSelectSnapshot1 = useCallback(
    (id) => {
      setSelectedSnapshotId1(id);
      loadItemsForTwoSnapshots(id, selectedSnapshotId2);
    },
    [loadItemsForTwoSnapshots, selectedSnapshotId2]
  );
  const handleSelectSnapshot2 = useCallback(
    (id) => {
      setSelectedSnapshotId2(id);
      loadItemsForTwoSnapshots(selectedSnapshotId1, id);
    },
    [loadItemsForTwoSnapshots, selectedSnapshotId1]
  );

  // Apply same filters to assets list for local aggregations (Patrimony, Breakdown, Snapshots)
  const filteredAssetsBoard = React.useMemo(
    () =>
      assets.filter((asset) => {
        if (selectedAssetIdFilter && asset.id !== selectedAssetIdFilter) return false;
        if (selectedCategoryCodeFilter && asset.category_code !== selectedCategoryCodeFilter) return false;
        if (
          selectedLocationNameFilter &&
          (asset.location_name || '').trim() !== selectedLocationNameFilter.trim()
        ) {
          return false;
        }
        return true;
      }),
    [assets, selectedAssetIdFilter, selectedCategoryCodeFilter, selectedLocationNameFilter]
  );

  const rawHistory = summary?.history ?? [];
  const history = React.useMemo(
    () => rawHistory.map((h) => ({ ...h, total_market: h.total_market ?? h.total })),
    [rawHistory]
  );

  const selectedSnapshot1 = snapshotList.find((s) => s.id === selectedSnapshotId1) ?? null;

  const totalFromSnapshot1 = React.useMemo(() => {
    if (!filteredAssetsBoard.length) return 0;
    const allowedIds = new Set(filteredAssetsBoard.map((a) => a.id));
    return Object.entries(snapshotMap).reduce((sum, [assetId, o]) => {
      const idNum = Number(assetId);
      if (!allowedIds.has(idNum)) return sum;
      return sum + (parseFloat(o?.snapshot_value) || 0);
    }, 0);
  }, [snapshotMap, filteredAssetsBoard]);

  const totalMarketFromSnapshot1 = React.useMemo(() => {
    if (!filteredAssetsBoard.length) return 0;
    const allowedIds = new Set(filteredAssetsBoard.map((a) => a.id));
    return Object.entries(snapshotMap).reduce((sum, [assetId, o]) => {
      const idNum = Number(assetId);
      if (!allowedIds.has(idNum)) return sum;
      return sum + (parseFloat(o?.market_value ?? o?.snapshot_value) || 0);
    }, 0);
  }, [snapshotMap, filteredAssetsBoard]);
  const byCategoryFromSnapshot1 = React.useMemo(() => {
    const hierarchy = buildHierarchy(filteredAssetsBoard, categories);
    const sortedL1 = sortedLevel1Keys(hierarchy);
    return sortedL1
      .map((l1) => {
        const assetsInL1 = Object.keys(hierarchy[l1] || {}).flatMap((l2) => hierarchy[l1][l2] || []);
        const total = sumSnapshotValue(assetsInL1, snapshotMap);
        return { category_code: l1, total };
      })
      .filter((x) => x.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [filteredAssetsBoard, categories, snapshotMap]);

  const byCategoryMarketFromSnapshot1 = React.useMemo(() => {
    const hierarchy = buildHierarchy(filteredAssetsBoard, categories);
    const sortedL1 = sortedLevel1Keys(hierarchy);
    return sortedL1
      .map((l1) => {
        const assetsInL1 = Object.keys(hierarchy[l1] || {}).flatMap((l2) => hierarchy[l1][l2] || []);
        const total = sumMarketValue(assetsInL1, snapshotMap);
        return { category_code: l1, total };
      })
      .filter((x) => x.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [filteredAssetsBoard, categories, snapshotMap]);

  const byLocationFromSnapshot1 = React.useMemo(() => {
    const byLoc = {};
    filteredAssetsBoard.forEach((asset) => {
      const loc = asset.location_name?.trim() || '—';
      if (!byLoc[loc]) byLoc[loc] = [];
      byLoc[loc].push(asset);
    });
    return Object.entries(byLoc)
      .map(([location_name, assetsInLoc]) => ({
        location_name,
        total: sumSnapshotValue(assetsInLoc, snapshotMap),
      }))
      .filter((x) => x.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [filteredAssetsBoard, snapshotMap]);

  const byLocationMarketFromSnapshot1 = React.useMemo(() => {
    const byLoc = {};
    filteredAssetsBoard.forEach((asset) => {
      const loc = asset.location_name?.trim() || '—';
      if (!byLoc[loc]) byLoc[loc] = [];
      byLoc[loc].push(asset);
    });
    return Object.entries(byLoc)
      .map(([location_name, assetsInLoc]) => ({
        location_name,
        total: sumMarketValue(assetsInLoc, snapshotMap),
      }))
      .filter((x) => x.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [filteredAssetsBoard, snapshotMap]);

  const bySubCategoryFromSnapshot1 = React.useMemo(() => {
    const hierarchy = buildHierarchy(filteredAssetsBoard, categories);
    const sortedL1 = sortedLevel1Keys(hierarchy);
    const level2Grouping = buildLevel2Grouping(hierarchy, sortedL1);
    const sortedL2 = sortedLevel2KeysFromGrouping(level2Grouping);
    return sortedL2
      .map((l2) => ({
        subcategory_code: l2,
        total: sumSnapshotValue(level2Grouping[l2] || [], snapshotMap),
      }))
      .filter((x) => x.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [filteredAssetsBoard, categories, snapshotMap]);

  const bySubCategoryMarketFromSnapshot1 = React.useMemo(() => {
    const hierarchy = buildHierarchy(filteredAssetsBoard, categories);
    const sortedL1 = sortedLevel1Keys(hierarchy);
    const level2Grouping = buildLevel2Grouping(hierarchy, sortedL1);
    const sortedL2 = sortedLevel2KeysFromGrouping(level2Grouping);
    return sortedL2
      .map((l2) => ({
        subcategory_code: l2,
        total: sumMarketValue(level2Grouping[l2] || [], snapshotMap),
      }))
      .filter((x) => x.total > 0)
      .sort((a, b) => b.total - a.total);
  }, [filteredAssetsBoard, categories, snapshotMap]);

  return (
    <div className="pt-2 w-full">
      <BoardFilterAssets
        snapshotList={snapshotList}
        selectedSnapshotId1={selectedSnapshotId1}
        onSelectSnapshot1={handleSelectSnapshot1}
        valueMode={valueMode}
        onValueModeChange={(mode) => patchBoard({ valueMode: mode })}
        assets={assets}
        categories={categories}
        selectedAssetIdFilter={selectedAssetIdFilter}
        onChangeAssetFilter={setSelectedAssetIdFilter}
        selectedCategoryCodeFilter={selectedCategoryCodeFilter}
        onChangeCategoryFilter={setSelectedCategoryCodeFilter}
        selectedLocationNameFilter={selectedLocationNameFilter}
        onChangeLocationFilter={setSelectedLocationNameFilter}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 w-full">
        {error && (
          <div className="col-span-full bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <CardTotalPatrimony
          total={totalFromSnapshot1}
          totalMarket={totalMarketFromSnapshot1}
          valueMode={valueMode}
          latestSnapshot={selectedSnapshot1}
          loading={loading}
          assets={filteredAssetsBoard}
          categories={categories}
          snapshotMap={snapshotMap}
        />
        <CardEvolutionHistory history={history} loading={loading} />
        <CardCategoryBreakdown
          byCategoryBase={byCategoryFromSnapshot1}
          byCategoryMarket={byCategoryMarketFromSnapshot1}
          byLocationBase={byLocationFromSnapshot1}
          byLocationMarket={byLocationMarketFromSnapshot1}
          bySubCategoryBase={bySubCategoryFromSnapshot1}
          bySubCategoryMarket={bySubCategoryMarketFromSnapshot1}
          valueMode={valueMode}
          total={totalFromSnapshot1}
          loading={loading}
          latestSnapshot={selectedSnapshot1}
          groupBy={groupBy}
          onGroupByChange={(next) => patchBoard({ groupBy: next })}
        />
        <BoardTableSnapshots
          assets={filteredAssetsBoard}
          categories={categories}
          snapshotList={snapshotList}
          selectedSnapshotId2={selectedSnapshotId2}
          onSelectSnapshot2={handleSelectSnapshot2}
          snapshotMaps={snapshotMaps}
          snapshotDates={snapshotDates}
          valueMode={valueMode}
          loading={loading}
          snapshotItemsLoading={false}
        />
      </div>

      <FabAssetsMenu assets={assets} onSuccess={refreshFull} />
    </div>
  );
};

export default AssetsOverview;
